import { useState, useEffect, useCallback } from 'react';
import { useSupabase } from './useSupabase';
import { useAuth } from './useAuth';
import type { Tables } from '@/integrations/supabase/types';

type DealershipRow = Tables<'dealerships'>;

interface UpdateDealershipData {
  name?: string;
  logo_url?: string;
  primary_color?: string;
  oem?: string;
  region?: string;
  // Theme-related fields
  theme_preset?: string;
  theme_overrides?: Record<string, unknown>;
  dark_mode_preference?: string;
}

export const useDealership = () => {
  const supabase = useSupabase();
  const { userId } = useAuth();
  const [dealership, setDealership] = useState<DealershipRow | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchDealership = useCallback(async () => {
    if (!userId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // First get the user's profile to find their dealership_id
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('dealership_id')
        .eq('user_id', userId)
        .maybeSingle();

      if (profileError) {
        throw new Error(profileError.message);
      }

      if (!profile || !profile.dealership_id) {
        setDealership(null);
        setLoading(false);
        return;
      }

      // Fetch the dealership. Use maybeSingle so an orphaned dealership_id
      // (row deleted) yields null instead of throwing a PGRST116 error.
      const { data: dealershipData, error: dealershipError } = await supabase
        .from('dealerships')
        .select('*')
        .eq('id', profile.dealership_id)
        .maybeSingle();

      if (dealershipError) {
        throw new Error(dealershipError.message);
      }

      setDealership((dealershipData as unknown as DealershipRow | null) ?? null);
    } catch (err) {
      console.error('[useDealership] Error fetching dealership:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch dealership');
    } finally {
      setLoading(false);
    }
  }, [userId, supabase]);

  useEffect(() => {
    // Fire-and-forget: fetchDealership handles its own errors and sets error state.
    void fetchDealership();
  }, [fetchDealership]);

  const updateDealership = useCallback(async (data: UpdateDealershipData) => {
    if (!dealership) {
      throw new Error('No dealership to update');
    }

    const { error: updateError } = await supabase
      .from('dealerships')
      .update({
        ...data,
        updated_at: new Date().toISOString(),
      })
      .eq('id', dealership.id);

    if (updateError) {
      throw new Error(updateError.message);
    }

    // Refresh the data
    await fetchDealership();
  }, [dealership, supabase, fetchDealership]);

  const uploadLogo = useCallback(async (file: File): Promise<string> => {
    if (!dealership) {
      throw new Error('No dealership to upload logo for');
    }

    const fileExt = file.name.split('.').pop();
    const fileName = `${dealership.id}/logo.${fileExt}`;
    const filePath = `dealership-logos/${fileName}`;

    // Upload to Supabase Storage
    const { error: uploadError } = await supabase.storage
      .from('public')
      .upload(filePath, file, { upsert: true });

    if (uploadError) {
      throw new Error(uploadError.message);
    }

    // Get public URL
    const { data: urlData } = supabase.storage
      .from('public')
      .getPublicUrl(filePath);

    return urlData.publicUrl;
  }, [dealership, supabase]);

  return {
    dealership,
    loading,
    error,
    refresh: fetchDealership,
    updateDealership,
    uploadLogo,
  };
};

